import React from 'react';
import { ArrowLeft, Mail, Phone, Building2, FileText, MessageCircle, ChevronRight } from 'lucide-react';
import { useApp } from './AppContext';
import type { Screen } from '../App';

interface ContactsScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const ContactsScreen: React.FC<ContactsScreenProps> = ({ onNavigate, onBack }) => {
  const { getBackgroundClass } = useApp();

  const handleContactSupport = () => {
    // Username бота для связи (без символа @)
    const botUsername = 'BOXP_AIbot';
    const telegramWebUrl = `https://t.me/${botUsername}`;
    window.open(telegramWebUrl, '_blank');
  };

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button 
          onClick={onBack} 
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <Phone className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">
            Контакты
          </h1>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">
        
        {/* Main Contact Card */}
        <div className="glass-panel rounded-xl p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="h-6 w-6 sm:h-7 sm:w-7 glass-icon flex-shrink-0" />
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold glass-text">Информация о проекте</h2>
          </div>

          <div className="space-y-6">
            {/* Organization Name */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 glass-icon flex-shrink-0" />
                <h3 className="text-sm sm:text-base font-semibold glass-text">Наименование</h3>
              </div>
              <p className="glass-text-secondary text-sm sm:text-base ml-7 leading-relaxed">
                ВОХР ТЕСТ — Образовательная платформа для подготовки сотрудников ведомственной охраны
              </p>
            </div>

            {/* Блок юридических данных — обязателен для модерации РСЯ (ИНН, адрес, email) */}
            <div className="glass-panel rounded-lg p-4">
              <h3 className="text-sm sm:text-base font-semibold glass-text mb-3">📝 Информация о владельце</h3>
              <ul className="space-y-2 glass-text-secondary text-xs sm:text-sm">
                <li className="flex gap-2">
                  <span className="font-medium glass-text flex-shrink-0">Владелец:</span>
                  <span>Батюшкин Максим Викторович</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium glass-text flex-shrink-0">ИНН:</span>
                  <span>380105121467</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium glass-text flex-shrink-0">Адрес:</span>
                  <span>664000, Россия, г. Иркутск</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium glass-text flex-shrink-0">Email:</span>
                  <a href="mailto:support@vohr-test.ru" className="underline underline-offset-2 hover:opacity-80 transition-opacity">support@vohr-test.ru</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
          
          {/* Telegram Support */}
          <div 
            className="glass-panel rounded-xl p-4 sm:p-6 glass-interactive cursor-pointer"
            onClick={handleContactSupport}
          >
            <div className="flex items-center gap-3 mb-3">
              <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-semibold glass-text">Telegram</h3>
            </div>
            <p className="glass-text-secondary text-sm sm:text-base mb-3 leading-relaxed">
              Служба поддержки через Telegram
            </p>
            <button 
              onClick={handleContactSupport}
              className="glass-panel rounded-lg p-2 text-xs sm:text-sm glass-text glass-interactive cursor-pointer w-full text-left flex items-center justify-between group"
            >
              <strong>@BOXP_AIbot</strong>
              <ChevronRight className="h-4 w-4 glass-icon flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>

          {/* Email - PLACEHOLDER */}
          <div className="glass-panel rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-semibold glass-text">Email</h3>
            </div>
            <p className="glass-text-secondary text-sm sm:text-base mb-3 leading-relaxed">
              Напишите нам на электронную почту
            </p>
            <a
              href="mailto:support@vohr-test.ru"
              className="glass-panel rounded-lg p-2 text-xs sm:text-sm glass-text glass-interactive cursor-pointer w-full text-left flex items-center justify-between group"
            >
              <strong>support@vohr-test.ru</strong>
              <ChevronRight className="h-4 w-4 glass-icon flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

        </div>

        {/* Related Pages */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <button
            onClick={() => onNavigate('about')}
            className="glass-panel rounded-xl p-4 glass-interactive cursor-pointer text-left flex items-center justify-between group"
          >
            <div>
              <h4 className="text-sm sm:text-base font-semibold glass-text mb-1">О проекте</h4>
              <p className="glass-text-secondary text-xs sm:text-sm">Узнайте больше о ВОХР ТЕСТ</p>
            </div>
            <ChevronRight className="h-5 w-5 glass-icon flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
          </button>
          
          <button
            onClick={() => onNavigate('privacy')}
            className="glass-panel rounded-xl p-4 glass-interactive cursor-pointer text-left flex items-center justify-between group"
          >
            <div>
              <h4 className="text-sm sm:text-base font-semibold glass-text mb-1">Конфиденциальность</h4>
              <p className="glass-text-secondary text-xs sm:text-sm">Как мы защищаем ваши данные</p>
            </div>
            <ChevronRight className="h-5 w-5 glass-icon flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>

      </div>
    </div>
  );
};
